import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import apiClient, { getErrorMessage } from '../../api/client';
import {
  FiCalendar,
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
  FiDownload,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiUsers,
  FiLogOut,
  FiActivity,
  FiServer,
  FiSave,
  FiX
} from 'react-icons/fi';

// Custom styles for content area
const pageStyles = `
  .dashboard-page {
    width: 100%;
  }

  .page-header {
    margin-bottom: 1.5rem;
  }

  .page-header h1 {
    font-size: 1.8rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 0.5rem 0;
  }

  .page-header p {
    font-size: 0.95rem;
    color: #6b7280;
    margin: 0;
  }

  .page-section {
    background: white;
    border-radius: 8px;
    padding: 1.25rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    border: 1px solid #e5e7eb;
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .section-header h2 {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
  }

  .form-section {
    max-width: 900px;
  }

  .form-field {
    margin-bottom: 1.25rem;
  }

  .form-field label {
    display: block;
    font-size: 0.9rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .form-field input,
  .form-field textarea,
  .form-field select {
    width: 100%;
    padding: 0.85rem 1rem;
    border: 1.5px solid #e5e7eb;
    border-radius: 6px;
    font-size: 0.95rem;
    font-family: inherit;
    transition: all 0.2s ease;
  }

  .form-field input:focus,
  .form-field textarea:focus,
  .form-field select:focus {
    outline: none;
    border-color: #3b82f6;
    background: #f0f9ff;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
  }

  .form-field.full-width {
    grid-column: 1 / -1;
  }

  .form-actions {
    display: flex;
    gap: 1rem;
    margin-top: 2rem;
  }

  .two-column {
    display: grid;
    grid-template-columns: 1fr 280px;
    gap: 1.5rem;
  }

  .left-column {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .right-column {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .stats-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .stat-box {
    background: white;
    border-radius: 8px;
    padding: 1.25rem;
    text-align: center;
    border: 1px solid #e5e7eb;
    transition: all 0.2s ease;
  }

  .stat-box:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .stat-box .icon {
    font-size: 2rem;
    margin-bottom: 0.75rem;
  }

  .stat-box .label {
    font-size: 0.85rem;
    color: #6b7280;
    margin-bottom: 0.5rem;
  }

  .stat-box .value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
  }

  .table-container {
    overflow-x: auto;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
  }

  .data-table {
    width: 100%;
    border-collapse: collapse;
  }

  .data-table th {
    background: #f9fafb;
    padding: 1rem;
    text-align: left;
    font-weight: 700;
    color: #374151;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-bottom: 1px solid #e5e7eb;
  }

  .data-table td {
    padding: 1rem;
    border-bottom: 1px solid #f3f4f6;
    font-size: 0.95rem;
  }

  .data-table tbody tr:hover {
    background: #f9fafb;
  }

  .badge {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.4rem 0.8rem;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: capitalize;
  }

  .actions {
    display: flex;
    gap: 0.5rem;
  }

  .action-icon {
    width: 32px;
    height: 32px;
    border-radius: 5px;
    border: none;
    background: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
  }

  .action-icon.edit {
    color: #10b981;
    background: rgba(16, 185, 129, 0.1);
  }

  .action-icon.edit:hover {
    background: rgba(16, 185, 129, 0.2);
  }

  .action-icon.delete {
    color: #ef4444;
    background: rgba(239, 68, 68, 0.1);
  }

  .action-icon.delete:hover {
    background: rgba(239, 68, 68, 0.2);
  }

  .action-icon:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  @media (max-width: 1024px) {
    .two-column {
      grid-template-columns: 1fr;
    }

    .right-column {
      grid-template-columns: repeat(2, 1fr);
      grid-auto-flow: dense;
    }
  }

  @media (max-width: 768px) {
    .stats-row {
      grid-template-columns: repeat(2, 1fr);
      gap: 0.75rem;
    }

    .form-grid {
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    .form-actions {
      flex-direction: column;
    }

    .form-actions .btn {
      width: 100%;
    }

    .page-header h1 {
      font-size: 1.5rem;
    }

    .section-header {
      flex-direction: column;
      align-items: flex-start;
    }
  }
`;

// Inject styles
const styleSheet = document.createElement('style');
styleSheet.textContent = pageStyles;
document.head.appendChild(styleSheet);

// ============================================
// CLIENT DASHBOARD
// ============================================
export const ClientDashboard = () => {
  const { organisations } = useAuth();

  const [payeReference, setPayeReference] = useState(null);
  const [headcount, setHeadcount] = useState(null);

  const [payrolls] = useState([
    { id: 1, period: 'Jan 2024', dueDate: '2024-01-31', status: 'submitted', employees: 12, amount: '£24,500' },
    { id: 2, period: 'Feb 2024', dueDate: '2024-02-28', status: 'pending', employees: 12, amount: '£24,500' },
    { id: 3, period: 'Mar 2024', dueDate: '2024-03-31', status: 'overdue', employees: 12, amount: '£24,500' }
  ]);

  useEffect(() => {
    let cancelled = false;

    apiClient.get('/payroll/schemes/paye')
      .then(({ data }) => {
        if (!cancelled) setPayeReference(data.data?.[0]?.payeReference ?? null);
      })
      .catch(() => { if (!cancelled) setPayeReference(null); });

    apiClient.get('/dashboards/hr/overview')
      .then(({ data }) => {
        if (!cancelled) setHeadcount(data.data.totalActiveHeadcount);
      })
      .catch(() => { if (!cancelled) setHeadcount(null); });

    return () => { cancelled = true; };
  }, []);

  const companyName = organisations?.[0]?.legalName || 'Your Company';

  const getStatusColor = (status) => {
    const colors = { submitted: '#10b981', pending: '#f59e0b', overdue: '#ef4444' };
    return colors[status] || '#6b7280';
  };

  const getStatusIcon = (status) => {
    const icons = {
      submitted: <FiCheckCircle size={16} />,
      pending: <FiClock size={16} />,
      overdue: <FiAlertCircle size={16} />
    };
    return icons[status];
  };

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <motion.div className="stats-row" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        {[
          { label: 'Total Submitted', value: '12', icon: FiCheckCircle, color: '#10b981' },
          { label: 'Pending', value: '2', icon: FiClock, color: '#f59e0b' },
          { label: 'Next Due', value: '31 Mar', icon: FiCalendar, color: '#3b82f6' },
          { label: 'Total Employees', value: headcount ?? '—', icon: FiUsers, color: '#8b5cf6' }
        ].map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <motion.div key={idx} className="stat-box" whileHover={{ y: -5 }}>
              <div className="icon" style={{ color: stat.color }}>
                <Icon size={24} />
              </div>
              <div className="label">{stat.label}</div>
              <div className="value">{stat.value}</div>
            </motion.div>
          );
        })}
      </motion.div>

      <div className="two-column">
        <div className="left-column">
          <motion.section className="page-section" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <div className="section-header">
              <h2>Company Profile</h2>
              <Link to="/dashboard/admin/organisation" className="btn btn-sm btn-outline">
                <FiEdit2 size={14} />
                Edit
              </Link>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
              <div>
                <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0 0 0.25rem 0', textTransform: 'uppercase' }}>Company Name</p>
                <p style={{ fontSize: '1rem', color: '#1f2937', margin: 0, fontWeight: 500 }}>{companyName}</p>
              </div>
              <div>
                <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0 0 0.25rem 0', textTransform: 'uppercase' }}>PAYE Reference</p>
                <p style={{ fontSize: '1rem', color: '#1f2937', margin: 0, fontWeight: 500 }}>{payeReference || 'Not set up yet'}</p>
              </div>
              <div>
                <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0 0 0.25rem 0', textTransform: 'uppercase' }}>Tax Year</p>
                <p style={{ fontSize: '1rem', color: '#1f2937', margin: 0, fontWeight: 500 }}>2023/2024</p>
              </div>
              <div>
                <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0 0 0.25rem 0', textTransform: 'uppercase' }}>Employees</p>
                <p style={{ fontSize: '1rem', color: '#1f2937', margin: 0, fontWeight: 500 }}>{headcount ?? '—'}</p>
              </div>
            </div>
          </motion.section>

          <motion.section className="page-section" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
            <div className="section-header">
              <h2>Recent Submissions</h2>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Link to="/dashboard/payroll/new" className="btn btn-sm btn-primary">
                  <FiPlus size={14} />
                  New Run
                </Link>
                <Link to="/dashboard/payroll" className="btn btn-sm btn-outline">
                  View All
                </Link>
              </div>
            </div>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Due Date</th>
                    <th>Status</th>
                    <th>Employees</th>
                    <th>Amount</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payrolls.map((payroll, idx) => (
                    <motion.tr key={payroll.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.05 }}>
                      <td style={{ fontWeight: 600 }}>{payroll.period}</td>
                      <td>{new Date(payroll.dueDate).toLocaleDateString()}</td>
                      <td>
                        <span className="badge" style={{ borderLeft: `3px solid ${getStatusColor(payroll.status)}`, color: getStatusColor(payroll.status) }}>
                          {getStatusIcon(payroll.status)}
                          {payroll.status}
                        </span>
                      </td>
                      <td>{payroll.employees}</td>
                      <td style={{ fontWeight: 600 }}>{payroll.amount}</td>
                      <td>
                        <div className="actions">
                          <button className="action-icon edit" title="Download">
                            <FiDownload size={16} />
                          </button>
                          <button className="action-icon edit" title="Edit">
                            <FiEdit2 size={16} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.section>
        </div>

        <div className="right-column">
          <motion.section className="page-section" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <div className="section-header" style={{ marginBottom: '1rem' }}>
              <h2>Reminders</h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              {[
                { date: 'Mar 31', title: 'March Payroll Due', priority: 'high' },
                { date: 'Apr 15', title: 'Q1 Compliance Review', priority: 'medium' },
                { date: 'May 20', title: 'Annual Audit', priority: 'medium' }
              ].map((reminder, idx) => (
                <div key={idx} style={{
                  padding: '0.75rem',
                  borderLeft: `3px solid ${reminder.priority === 'high' ? '#ef4444' : '#f59e0b'}`,
                  background: reminder.priority === 'high' ? 'rgba(239, 68, 68, 0.05)' : 'rgba(245, 158, 11, 0.05)',
                  borderRadius: '4px'
                }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#374151' }}>{reminder.date}</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1f2937' }}>{reminder.title}</div>
                </div>
              ))}
            </div>
          </motion.section>

          <motion.section className="page-section" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
            <div className="section-header" style={{ marginBottom: '1rem' }}>
              <h2>System Status</h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              {[
                { label: 'API Status', value: 'Operational', status: 'excellent' },
                { label: 'Database', value: 'Healthy', status: 'excellent' },
                { label: 'Response Time', value: '120ms', status: 'good' },
                { label: 'Storage', value: '45% Used', status: 'good' }
              ].map((health, idx) => (
                <div key={idx} style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>{health.label}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.95rem', fontWeight: 600, color: health.status === 'excellent' ? '#10b981' : '#3b82f6' }}>
                      {health.value}
                    </span>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: health.status === 'excellent' ? '#10b981' : '#3b82f6'
                    }}></div>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>

          <motion.section className="page-section" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
            <div className="section-header" style={{ marginBottom: '1rem' }}>
              <h2>Activity Feed</h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {[
                { user: 'You', action: 'Signed in', time: 'Now' },
                { user: 'System', action: 'Backup completed', time: '2h ago' },
                { user: 'Admin', action: 'Profile updated', time: '1d ago' }
              ].map((activity, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '0.6rem', padding: '0.6rem', background: '#f9fafb', borderRadius: '6px' }}>
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    flexShrink: 0
                  }}>
                    {activity.user[0]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1f2937', margin: 0 }}>{activity.user}</p>
                    <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: 0 }}>{activity.action}</p>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>{activity.time}</p>
                </div>
              ))}
            </div>
          </motion.section>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================
// PAYROLL WIZARD (was PayrollForm)
// ============================================
export const PayrollForm = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [payeSchemes, setPayeSchemes] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState('');

  const [newSchemeForm, setNewSchemeForm] = useState({
    payeReference: '', accountsOfficeReference: '', taxYearStart: ''
  });
  const [newPeriodForm, setNewPeriodForm] = useState({
    payeSchemeId: '', frequency: 'monthly', periodNumber: 1,
    taxYear: '', startDate: '', endDate: '', paymentDate: '', isRecurring: false,
  });
  const [showNewPeriodForm, setShowNewPeriodForm] = useState(false);

  const [run, setRun] = useState(null);
  const [runEmployees, setRunEmployees] = useState([]);
  const [employeeNames, setEmployeeNames] = useState({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apiClient.get('/payroll/schemes/paye'),
      apiClient.get('/payroll/periods'),
    ])
      .then(([schemesRes, periodsRes]) => {
        if (cancelled) return;
        setPayeSchemes(schemesRes.data.data);
        setPeriods(periodsRes.data.data);
        if (schemesRes.data.data.length > 0) {
          setNewPeriodForm((prev) => ({ ...prev, payeSchemeId: schemesRes.data.data[0].id }));
        }
      })
      .catch((err) => showError(getErrorMessage(err, 'Could not load payroll setup')))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [showError]);

  const handleCreateScheme = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await apiClient.post('/payroll/schemes/paye', newSchemeForm);
      setPayeSchemes([data.data]);
      setNewPeriodForm((prev) => ({ ...prev, payeSchemeId: data.data.id }));
      success('PAYE scheme created');
    } catch (err) {
      showError(getErrorMessage(err, 'Could not create PAYE scheme'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePeriod = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await apiClient.post('/payroll/periods', newPeriodForm);
      setPeriods((prev) => [...prev, data.data]);
      setSelectedPeriodId(data.data.id);
      setShowNewPeriodForm(false);
      success('Payroll period created');
    } catch (err) {
      showError(getErrorMessage(err, 'Could not create payroll period'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRun = async () => {
    if (!selectedPeriodId) {
      showError('Select or create a payroll period first');
      return;
    }
    setLoading(true);
    try {
      const { data } = await apiClient.post('/payroll/runs', { payrollPeriodId: selectedPeriodId });
      setRun(data.data);
      setStep(2);
    } catch (err) {
      showError(getErrorMessage(err, 'Could not create payroll run'));
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    setLoading(true);
    try {
      await apiClient.post(`/payroll/runs/${run.id}/calculate`);
      const [runEmployeesRes, employeesRes] = await Promise.all([
        apiClient.get(`/payroll/runs/${run.id}/employees`),
        apiClient.get('/hr/employees'),
      ]);
      const nameMap = {};
      employeesRes.data.data.forEach((e) => { nameMap[e.id] = `${e.firstName} ${e.lastName}`; });
      setEmployeeNames(nameMap);
      setRunEmployees(runEmployeesRes.data.data);
      setStep(3);
    } catch (err) {
      showError(getErrorMessage(err, 'Calculation failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.post(`/payroll/runs/${run.id}/approve`);
      setRun(data.data);
      setStep(4);
    } catch (err) {
      showError(getErrorMessage(err, 'Approval failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleLock = async () => {
    setLoading(true);
    try {
      await apiClient.post(`/payroll/runs/${run.id}/lock`);
      success('✅ Payroll run locked — payslips generated');
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (err) {
      showError(getErrorMessage(err, 'Locking failed'));
    } finally {
      setLoading(false);
    }
  };

  const totalGross = runEmployees.reduce((sum, e) => sum + Number(e.grossPay), 0);
  const totalNet = runEmployees.reduce((sum, e) => sum + Number(e.netPay), 0);

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <motion.div className="page-header" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
        <h1>New Payroll Run</h1>
        <p>Step {step} of 4</p>
      </motion.div>

      {step === 1 && (
        <motion.section className="page-section form-section" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          {payeSchemes.length === 0 ? (
            <>
              <div className="section-header"><h2>Set Up Your PAYE Scheme</h2></div>
              <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                You need a PAYE scheme on file before running payroll for the first time.
              </p>
              <form onSubmit={handleCreateScheme} className="form-grid">
                <div className="form-field">
                  <label>PAYE Reference *</label>
                  <input required value={newSchemeForm.payeReference}
                    onChange={(e) => setNewSchemeForm(p => ({ ...p, payeReference: e.target.value }))}
                    placeholder="123/AB456" />
                </div>
                <div className="form-field">
                  <label>Accounts Office Reference *</label>
                  <input required value={newSchemeForm.accountsOfficeReference}
                    onChange={(e) => setNewSchemeForm(p => ({ ...p, accountsOfficeReference: e.target.value }))}
                    placeholder="123PA00012345" />
                </div>
                <div className="form-field">
                  <label>Tax Year Start *</label>
                  <input type="date" required value={newSchemeForm.taxYearStart}
                    onChange={(e) => setNewSchemeForm(p => ({ ...p, taxYearStart: e.target.value }))} />
                </div>
                <div className="form-field full-width form-actions">
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : 'Save PAYE Scheme'}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <div className="section-header"><h2>Choose a Payroll Period</h2></div>

              {periods.length > 0 && (
                <div className="form-field">
                  <label>Existing periods</label>
                  <select value={selectedPeriodId} onChange={(e) => setSelectedPeriodId(e.target.value)}>
                    <option value="">-- select a period --</option>
                    {periods.map((p) => (
                      <option key={p.id} value={p.id}>
                        Period {p.periodNumber} ({p.taxYear}) — pays {new Date(p.paymentDate).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {!showNewPeriodForm ? (
                <button type="button" className="btn btn-outline" onClick={() => setShowNewPeriodForm(true)} style={{ marginTop: '0.5rem' }}>
                  <FiPlus size={16} /> Create a new period instead
                </button>
              ) : (
                <form onSubmit={handleCreatePeriod} className="form-grid" style={{ marginTop: '1rem' }}>
                  <div className="form-field">
                    <label>Frequency *</label>
                    <select value={newPeriodForm.frequency}
                      onChange={(e) => setNewPeriodForm(p => ({ ...p, frequency: e.target.value }))}>
                      <option value="monthly">Monthly</option>
                      <option value="fortnightly">Fortnightly</option>
                      <option value="weekly">Weekly</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Period Number *</label>
                    <input type="number" min="1" required value={newPeriodForm.periodNumber}
                      onChange={(e) => setNewPeriodForm(p => ({ ...p, periodNumber: Number(e.target.value) }))} />
                  </div>
                  <div className="form-field">
                    <label>Tax Year *</label>
                    <input required placeholder="2026-2027" value={newPeriodForm.taxYear}
                      onChange={(e) => setNewPeriodForm(p => ({ ...p, taxYear: e.target.value }))} />
                  </div>
                  <div className="form-field">
                    <label>Start Date *</label>
                    <input type="date" required value={newPeriodForm.startDate}
                      onChange={(e) => setNewPeriodForm(p => ({ ...p, startDate: e.target.value }))} />
                  </div>
                  <div className="form-field">
                    <label>End Date *</label>
                    <input type="date" required value={newPeriodForm.endDate}
                      onChange={(e) => setNewPeriodForm(p => ({ ...p, endDate: e.target.value }))} />
                  </div>
                  <div className="form-field">
                    <label>Payment Date *</label>
                    <input type="date" required value={newPeriodForm.paymentDate}
                      onChange={(e) => setNewPeriodForm(p => ({ ...p, paymentDate: e.target.value }))} />
                  </div>
                  <div className="form-field full-width">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textTransform: 'none' }}>
                      <input
                        type="checkbox"
                        checked={newPeriodForm.isRecurring}
                        onChange={(e) => setNewPeriodForm(p => ({ ...p, isRecurring: e.target.checked }))}
                        style={{ width: 'auto' }}
                      />
                      Make this recurring
                    </label>
                    <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.3rem' }}>
                      Each subsequent period + payroll run will be auto-created and auto-calculated 5 days before payment date. You'll still need to manually Approve and Lock each one.
                    </p>
                  </div>
                  <div className="form-field full-width form-actions">
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? 'Creating...' : 'Create Period'}
                    </button>
                    <button type="button" className="btn btn-outline" onClick={() => setShowNewPeriodForm(false)}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              <div className="form-actions">
                <button type="button" className="btn btn-primary" disabled={loading || !selectedPeriodId} onClick={handleCreateRun}>
                  {loading ? 'Creating Run...' : 'Continue'} <FiPlus size={16} />
                </button>
                <button type="button" className="btn btn-outline" onClick={() => navigate('/dashboard')}>Cancel</button>
              </div>
            </>
          )}
        </motion.section>
      )}

      {step === 2 && run && (
        <motion.section className="page-section" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="section-header"><h2>Run Created — Ready to Calculate</h2></div>
          <p style={{ color: '#6b7280' }}>Status: <strong>{run.status}</strong></p>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
            This calculates gross pay, tax, NI, and pension for every active employee based on their salary on file.
          </p>
          <div className="form-actions">
            <button type="button" className="btn btn-primary" disabled={loading} onClick={handleCalculate}>
              {loading ? 'Calculating...' : 'Calculate Payroll'}
            </button>
          </div>
        </motion.section>
      )}

      {step === 3 && (
        <motion.section className="page-section" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="section-header"><h2>Review Calculated Pay</h2></div>
          <div className="stats-row">
            <div className="stat-box"><div className="label">Employees</div><div className="value">{runEmployees.length}</div></div>
            <div className="stat-box"><div className="label">Total Gross</div><div className="value">£{totalGross.toFixed(2)}</div></div>
            <div className="stat-box"><div className="label">Total Net</div><div className="value">£{totalNet.toFixed(2)}</div></div>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr><th>Employee</th><th>Gross Pay</th><th>Deductions</th><th>Net Pay</th></tr>
              </thead>
              <tbody>
                {runEmployees.map((re) => (
                  <tr key={re.id}>
                    <td>{employeeNames[re.employeeId] || re.employeeId}</td>
                    <td>£{Number(re.grossPay).toFixed(2)}</td>
                    <td>£{Number(re.totalDeductions).toFixed(2)}</td>
                    <td style={{ fontWeight: 600 }}>£{Number(re.netPay).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-primary" disabled={loading} onClick={handleApprove}>
              {loading ? 'Approving...' : 'Approve Payroll Run'}
            </button>
          </div>
        </motion.section>
      )}

      {step === 4 && (
        <motion.section className="page-section" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="section-header"><h2>Approved — Final Step</h2></div>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
            Locking generates payslips for every employee and posts this run to the general ledger.
            This can't be undone.
          </p>
          <div className="form-actions">
            <button type="button" className="btn btn-primary" disabled={loading} onClick={handleLock}>
              {loading ? 'Locking...' : 'Lock Payroll Run'}
            </button>
          </div>
        </motion.section>
      )}
    </motion.div>
  );
};

// ============================================
// USER MANAGEMENT
// ============================================
export const UserManagement = () => {
  const { success, error: showError } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', firstName: '', lastName: '', roleCode: 'employee' });

  const [editingUserId, setEditingUserId] = useState(null);
  const [editRole, setEditRole] = useState('employee');

  const loadUsers = () => {
    setLoading(true);
    apiClient.get('/organisations/current/users')
      .then(({ data }) => setUsers(data.data))
      .catch((err) => showError(getErrorMessage(err, 'Could not load users')))
      .finally(() => setLoading(false));
  };

  useEffect(loadUsers, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/organisations/current/users/invite', inviteForm);
      success('✅ User invited');
      setShowInvite(false);
      setInviteForm({ email: '', firstName: '', lastName: '', roleCode: 'employee' });
      loadUsers();
    } catch (err) {
      showError(getErrorMessage(err, 'Could not invite user'));
    }
  };

  const startEditRole = (u) => {
    setEditingUserId(u.userId);
    setEditRole(u.roleCodes[0] || 'employee');
  };

  const handleUpdateRole = async (userId) => {
    try {
      await apiClient.patch(`/organisations/current/users/${userId}`, { roleCode: editRole });
      success('✅ Role updated');
      setEditingUserId(null);
      loadUsers();
    } catch (err) {
      showError(getErrorMessage(err, 'Could not update role'));
    }
  };

  // There's no hard-delete endpoint — only suspend/reactivate via status.
  const toggleSuspend = async (userId, currentStatus) => {
    const nextStatus = currentStatus === 'active' ? 'suspended' : 'active';
    try {
      await apiClient.patch(`/organisations/current/users/${userId}`, { status: nextStatus });
      success(`User ${nextStatus}`);
      loadUsers();
    } catch (err) {
      showError(getErrorMessage(err, 'Could not update user'));
    }
  };

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <motion.div className="page-header" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
        <h1>User Management</h1>
        <p>Manage system users and permissions</p>
      </motion.div>

      <motion.section className="page-section" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
        <div className="section-header">
          <h2>Users</h2>
          <button
            className="btn btn-primary"
            style={{ width: 'auto', flexShrink: 0, whiteSpace: 'nowrap' }}
            onClick={() => setShowInvite((s) => !s)}
          >
            <FiPlus size={18} />
            {showInvite ? 'Cancel' : 'Add User'}
          </button>
        </div>

        {showInvite && (
          <form onSubmit={handleInvite} className="form-grid" style={{ marginBottom: '1.5rem' }}>
            <div className="form-field">
              <label>First Name *</label>
              <input required value={inviteForm.firstName}
                onChange={(e) => setInviteForm(p => ({ ...p, firstName: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Last Name *</label>
              <input required value={inviteForm.lastName}
                onChange={(e) => setInviteForm(p => ({ ...p, lastName: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Email *</label>
              <input type="email" required value={inviteForm.email}
                onChange={(e) => setInviteForm(p => ({ ...p, email: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Role *</label>
              <select value={inviteForm.roleCode}
                onChange={(e) => setInviteForm(p => ({ ...p, roleCode: e.target.value }))}>
                <option value="employee">Employee</option>
                <option value="hr_manager">HR Manager</option>
                <option value="payroll_manager">Payroll Manager</option>
                <option value="accountant">Accountant</option>
              </select>
            </div>
            <div className="form-field full-width form-actions">
              <button type="submit" className="btn btn-primary">Send Invite</button>
            </div>
          </form>
        )}

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No users yet</td></tr>
              ) : users.map((u, idx) => (
                <motion.tr key={u.userId} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.05 }}>
                  <td style={{ fontWeight: 600 }}>{u.firstName} {u.lastName}</td>
                  <td>{u.email}</td>
                  <td>
                    {editingUserId === u.userId ? (
                      <select value={editRole} onChange={(e) => setEditRole(e.target.value)} style={{ padding: '0.3rem' }}>
                        <option value="employee">Employee</option>
                        <option value="hr_manager">HR Manager</option>
                        <option value="payroll_manager">Payroll Manager</option>
                        <option value="accountant">Accountant</option>
                        <option value="business_owner">Business Owner</option>
                      </select>
                    ) : (
                      <span className="badge" style={{ background: u.roleCodes.includes('business_owner') ? '#3b82f6' : '#10b981', color: 'white' }}>
                        {u.roleCodes.join(', ')}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="badge" style={{ background: u.status === 'active' ? '#10b981' : '#9ca3af', color: 'white' }}>
                      {u.status}
                    </span>
                  </td>
                  <td>
                    <div className="actions">
                      {editingUserId === u.userId ? (
                        <>
                          <button className="action-icon edit" title="Save" onClick={() => handleUpdateRole(u.userId)}>
                            <FiSave size={16} />
                          </button>
                          <button className="action-icon delete" title="Cancel" onClick={() => setEditingUserId(null)}>
                            <FiX size={16} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="action-icon edit" title="Edit role" onClick={() => startEditRole(u)} disabled={u.isPrimaryContact}>
                            <FiEdit2 size={16} />
                          </button>
                          <button
                            className="action-icon delete"
                            title={u.status === 'active' ? 'Suspend' : 'Reactivate'}
                            onClick={() => toggleSuspend(u.userId, u.status)}
                            disabled={u.isPrimaryContact}
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.section>
    </motion.div>
  );
};

// ============================================
// ADMIN DASHBOARD
// ============================================
export const AdminDashboard = () => {
  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>Admin Dashboard</h1>
        <p>System overview and management</p>
      </div>
      <section className="page-section">
        <p style={{ fontSize: '1.1rem', color: '#6b7280', textAlign: 'center', padding: '2rem' }}>Coming Soon...</p>
      </section>
    </motion.div>
  );
};